import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../contexts/useAuth';
import Card from '../components/Card';
import Button from '../components/Button';
import Input from '../components/Input';
import ErrorMessage from '../components/ErrorMessage';
import { LogIn, UserPlus, BookOpen } from 'lucide-react';

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, register, isLoading, isAuthenticated } = useAuth();
  
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Redirect if already authenticated (use useEffect to avoid render-time navigation)
  const from = (
    location.state as unknown as { from?: { pathname?: string } } | null
  )?.from?.pathname || '/';
  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, from]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      if (isLogin) {
        await login(email, password);
      } else {
        await register(email, password, username || undefined);
      }
      // Navigate to the page user was trying to access, or home
      navigate(from, { replace: true });
    } catch (err: unknown) {
      // Extract error message from various possible error formats
      let errorMessage = 'Произошла ошибка';
      
      const responseData = (err as { response?: { data?: unknown } } | undefined)?.response?.data;
      if (responseData && typeof responseData === 'object') {
        const dataObj = responseData as { message?: unknown; error?: unknown };
        if (typeof dataObj.message === 'string') {
          errorMessage = dataObj.message;
        } else if (typeof dataObj.error === 'string') {
          errorMessage = dataObj.error;
        }
      } else if (typeof responseData === 'string') {
        errorMessage = responseData;
      } else if (err instanceof Error) {
        errorMessage = err.message || errorMessage;
      }
      
      // Translate common error messages
      if (errorMessage.includes('Invalid credentials') || errorMessage.includes('Invalid email or password')) {
        errorMessage = 'Неверный email или пароль';
      } else if (errorMessage.includes('User already exists')) {
        errorMessage = 'Пользователь с таким email уже существует';
      } else if (errorMessage.includes('Database') || errorMessage.includes('table does not exist')) {
        errorMessage = 'Ошибка конфигурации базы данных. Обратитесь к администратору.';
      } else if (errorMessage.includes('Network Error') || errorMessage.includes('ECONNREFUSED')) {
        errorMessage = 'Не удалось подключиться к серверу. Проверьте подключение к интернету.';
      }
      
      setError(errorMessage);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="flex justify-center">
            <BookOpen className="h-12 w-12 text-primary-600 dark:text-primary-400" />
          </div>
          <h2 className="mt-6 text-3xl font-bold text-gray-900 dark:text-white">
            {isLogin ? 'Вход в систему' : 'Регистрация'}
          </h2>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            {isLogin
              ? 'Войдите в аккаунт для продолжения'
              : 'Создайте новый аккаунт'}
          </p>
        </div>

        <Card>
          <form className="space-y-6" onSubmit={handleSubmit}>
            {error && <ErrorMessage message={error} />}

            {!isLogin && (
              <Input
                label="Имя пользователя"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Опционально"
              />
            )}

            <Input
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="your@email.com"
              autoComplete="email"
            />

            <Input
              label="Пароль"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="Минимум 6 символов"
              autoComplete={isLogin ? 'current-password' : 'new-password'}
              minLength={6}
            />

            <div className="flex flex-col space-y-3">
              <Button
                type="submit"
                disabled={isLoading || !email || !password}
                className="w-full"
              >
                {isLoading ? (
                  'Загрузка...'
                ) : isLogin ? (
                  <>
                    <LogIn className="h-4 w-4 mr-2" />
                    Войти
                  </>
                ) : (
                  <>
                    <UserPlus className="h-4 w-4 mr-2" />
                    Зарегистрироваться
                  </>
                )}
              </Button>

              <button
                type="button"
                onClick={() => {
                  setIsLogin(!isLogin);
                  setError(null);
                }}
                className="text-sm text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300"
              >
                {isLogin
                  ? 'Нет аккаунта? Зарегистрироваться'
                  : 'Уже есть аккаунт? Войти'}
              </button>
            </div>
          </form>
        </Card>

        <div className="text-center">
          <Link
            to="/"
            className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
          >
            Вернуться на главную
          </Link>
        </div>
      </div>
    </div>
  );
}
